import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Trash2, Save, Loader2, Mail, Info } from "lucide-react";
import { AppNav } from "@/components/AppNav";
import { UserAvatar } from "@/components/UserAvatar";
import { FacebookConnect } from "@/components/FacebookConnect";
import { MetaConnect } from "@/components/MetaConnect";
import { MetaReconnectBanner } from "@/components/MetaReconnectBanner";
import { ReferralCard } from "@/components/ReferralCard";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX = 5 * 1024 * 1024;
const input = "w-full rounded-lg border border-[#ccd0d5] bg-white px-3 py-2 text-sm outline-none focus:border-[#1877f2]";

function PhotoCard({ user, setUser }) {
  const fileRef = useRef(null);
  const [pct, setPct] = useState(null);
  const [removing, setRemoving] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!TYPES.includes(f.type)) return toast.error("Format non accepté : JPG, PNG ou WEBP uniquement");
    if (f.size > MAX) return toast.error("Photo trop lourde : 5 Mo maximum");
    setPct(0);
    try {
      setUser(await api.uploadAvatar(f, setPct));
      toast.success("Photo de profil mise à jour");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPct(null);
    }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      setUser(await api.deleteAvatar());
      toast.success("Photo supprimée");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setRemoving(false);
    }
  };

  const busy = pct !== null;
  return (
    <section className="card-surface p-6 flex flex-col sm:flex-row items-center gap-6" data-testid="profile-photo-card">
      <div className="relative">
        <UserAvatar user={user} size={120} testId="profile-avatar" />
        {busy && (
          <div className="absolute inset-0 rounded-full bg-black/55 grid place-items-center text-white text-sm font-bold" data-testid="profile-upload-progress">
            {pct < 100 ? `${pct}%` : <Loader2 className="w-6 h-6 animate-spin" />}
          </div>
        )}
      </div>
      <div className="flex-1 text-center sm:text-left">
        <h2 className="font-display text-lg font-bold">Photo de profil</h2>
        <p className="text-sm text-[#65676b] mt-1">JPG, PNG ou WEBP · 5 Mo max · recadrée automatiquement en carré.</p>
        <div className="mt-4 flex flex-wrap gap-2 justify-center sm:justify-start">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} data-testid="profile-avatar-input" />
          <button type="button" className="btn-primary" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="profile-avatar-upload-btn">
            <Camera className="w-4 h-4" /> {user?.avatar_url ? "Changer la photo" : "Télécharger une photo"}
          </button>
          {user?.avatar_url && (
            <button type="button" className="btn-ghost" disabled={busy || removing} onClick={remove} data-testid="profile-avatar-delete-btn">
              <Trash2 className="w-4 h-4" /> {removing ? "Suppression…" : "Supprimer"}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function InfoCard({ user, setUser }) {
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Le nom ne peut pas être vide");
    setSaving(true);
    try {
      setUser(await api.updateProfile({ name: name.trim() }));
      toast.success("Profil enregistré");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={save} className="card-surface p-6 space-y-4" data-testid="profile-info-form">
      <label className="block"><span className="text-xs font-semibold uppercase text-[#65676b]">Nom affiché</span>
        <input className={`${input} mt-1`} value={name} maxLength={60} onChange={e => setName(e.target.value)} data-testid="profile-name-input" /></label>
      <div><span className="text-xs font-semibold uppercase text-[#65676b]">Email</span>
        <div className="mt-1 flex items-center gap-2 rounded-lg bg-[#f0f2f5] px-3 py-2 text-sm text-[#65676b]" data-testid="profile-email">
          <Mail className="w-4 h-4" /> {user?.email}
        </div></div>
      <button type="submit" disabled={saving} className="btn-primary" data-testid="profile-save-btn">
        <Save className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
      </button>
    </form>
  );
}

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  return (
    <>
      <AppNav current="profile" />
      <main className="max-w-3xl mx-auto px-4 lg:px-8 py-8 space-y-6" data-testid="profile-page">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Mon profil & paramètres</h1>
          <p className="text-sm text-[#65676b] mt-1">Votre photo et votre nom signent vos vidéos.</p>
        </div>
        <MetaReconnectBanner />
        <PhotoCard user={user} setUser={setUser} />
        <FacebookConnect />
        <MetaConnect />
        <ReferralCard />
        <InfoCard user={user} setUser={setUser} />
        <p className="flex items-start gap-2 text-xs text-[#65676b]" data-testid="profile-video-note">
          <Info className="w-4 h-4 shrink-0" /> Votre photo apparaît en médaillon dans l'intro et la fin de vos « Résumé du jour », avec votre nom.
        </p>
      </main>
    </>
  );
}
